/**
 * The phone's one view button — Aaron, Oct 25, phone notes 2, 8 and 9.
 *
 * Beside the people button in Activity's second row, the same on every tab:
 * the view it is on and ▾, 44 px, and a tap opens a bottom sheet of the
 * choices — the Calendar's *Day · 3 days · Month · List*, the Inbox's *All ·
 * Texts · Email*, Tasks' *List · Board*. A pick applies and closes; what was
 * picked is remembered on this phone (§4.6), by whoever owns that part of
 * the state. A tab with nothing to choose (All activity) draws no button.
 *
 * The sheet is Activity's `BottomSheet`, the people sheet's (Board 13 C), with
 * the 52 px picker rows of §10.2 — never a native drop-down.
 */
import { useState } from 'react'
import { IconCheck } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { BottomSheet } from './sheet.js'

export interface ViewChoice<T extends string> {
  value: T
  label: string
}

export function ViewMenu<T extends string>({
  label, value, options, onChange,
}: {
  /** What is being chosen — the sheet's title and the button's name: *Calendar view*. */
  label: string
  value: T
  options: readonly ViewChoice<T>[]
  onChange: (value: T) => void
}) {
  const [open, setOpen] = useState(false)
  const current = options.find((option) => option.value === value) ?? options[0]
  if (!current) return null
  return (
    <>
      <button type="button" data-view-button aria-haspopup="dialog" aria-expanded={open}
        aria-label={`${label}: ${current.label}`} onClick={() => setOpen(true)}
        className="inline-flex h-11 shrink-0 items-center gap-1 rounded-[10px] border border-line bg-surface pl-3 pr-2 text-[13.5px] font-semibold text-text hover:bg-hover">
        <span className="whitespace-nowrap">{current.label}</span>
        {/* The people button's own chevron, so the two read as one pair. */}
        <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2.2}
          strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-muted" aria-hidden>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <BottomSheet title={label} sub="remembered on this phone" label={label} onClose={() => setOpen(false)}
          bodyClassName="px-2 pb-4 pt-2">
          <div role="radiogroup" aria-label={label} className="flex flex-col">
            {options.map((option) => {
              const on = option.value === current.value
              return (
                <button key={option.value} type="button" role="radio" aria-checked={on}
                  onClick={() => { setOpen(false); if (!on) onChange(option.value) }}
                  className={cn('flex min-h-[52px] w-full items-center gap-2.5 rounded-[10px] px-3 text-left text-[15px]',
                    on ? 'bg-accent-soft font-bold text-accent-dark' : 'font-semibold text-text')}>
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {on && <span className="inline-flex shrink-0 text-accent"><IconCheck size={18} /></span>}
                </button>
              )
            })}
          </div>
        </BottomSheet>
      )}
    </>
  )
}
